/*
 * What was true when the title screen went up.
 *
 * Three booleans and a module-level record, and the whole file exists because of
 * one claim that cannot be made any other way: **the title is the first frame,
 * and nothing about the world has happened yet** (chunk T, requirement T-1). A
 * self-check runs at the end of the boot sequence, by which time the world is
 * built and the loop is running, so by then there is nothing left to look at —
 * the only honest way to check an *order* is to write down what the order was as
 * it happened.
 *
 * It is the shape `document.body.dataset.selfcheck` already has one surface
 * over: a fact about the boot, left where something that was not there at the
 * time can read it (`main.ts`, `dev/selfcheck/title.ts`).
 *
 * Nothing here decides anything. A session that never calls {@link markTitleFrame}
 * — a build whose boot order regressed, a driver that opened a page and closed
 * it — reads as a title that was never shown, which is what the check is looking
 * for.
 */

/** What the boot sequence had done by the time the title screen was drawn. */
export interface TitleFrame {
  /**
   * Whether the screen was drawn at all.
   *
   * `false` is a session that declined it (`?title=0`), which is a real answer
   * and not a failure: three drivers open a dozen sessions between them and one
   * per driver walks the rail (`hud/title.ts`'s `titleRequested`).
   */
  readonly shown: boolean;

  /** Whether a world had been built when it went up. It must not have been. */
  readonly worldBuilt: boolean;

  /** Whether the debug handle was installed then. Nor that. */
  readonly handleInstalled: boolean;
}

/** Nothing has been drawn yet, which is what a session reads before its boot runs. */
const NOTHING: TitleFrame = { shown: false, worldBuilt: false, handleInstalled: false };

let frame: TitleFrame = NOTHING;

/**
 * Records what was true at the instant the title screen was put up.
 *
 * Called from `main.ts` at the one place that knows — with the world still
 * unbuilt, which is the fact being written down. `worldBuilt` is passed rather
 * than asked because this module holds no reference to a world and should not
 * grow one; the handle it checks itself, because `window.__deepWorld` is a
 * global and asking is cheaper than being told.
 */
export function markTitleFrame(shown: boolean, worldBuilt: boolean): void {
  frame = {
    shown,
    worldBuilt,
    handleInstalled: handleIsInstalled(),
  };
}

/** What was true then. See {@link markTitleFrame}. */
export function titleFrame(): TitleFrame {
  return frame;
}

/**
 * Whether `window.__deepWorld` exists.
 *
 * The debug handle is installed after the start menus close (`main.ts`), and
 * every headless driver in this tree waits on it as the signal that a session is
 * live (`scripts/live-smoke.mjs`) — so it existing at the title is the sharpest
 * possible statement that the boot ran in the wrong order.
 */
function handleIsInstalled(): boolean {
  return (window as unknown as Record<string, unknown>)["__deepWorld"] !== undefined;
}
